type SchedulePayload = Record<string, unknown>;

const SCHEDULE_LABEL_KEYS = [
  'verticals',
  'verticalNames',
  'systems',
  'systemNames',
  'vertical',
  'system',
] as const;

function flattenSchedulePayload(payload: SchedulePayload): SchedulePayload {
  const nested = payload.payload;
  if (!nested || typeof nested !== 'object' || Array.isArray(nested)) {
    return payload;
  }

  const inner = nested as SchedulePayload;
  const { payload: _ignored, ...outer } = payload;
  const flat: SchedulePayload = { ...outer, ...inner };

  for (const key of SCHEDULE_LABEL_KEYS) {
    if (inner[key] == null && outer[key] != null) {
      flat[key] = outer[key];
    }
  }

  delete flat.payload;

  return flat;
}

function collectStringLabels(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((item) => (typeof item === 'string' ? item.trim() : String(item ?? '').trim()))
    .filter((label) => label !== '');
}

function labelFromEntry(entry: unknown): string | null {
  if (!entry || typeof entry !== 'object') {
    return null;
  }

  const row = entry as Record<string, unknown>;
  const label = [row.description, row.name, row.label, row.code]
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .find((value) => value !== '');

  return label ?? null;
}

function resolveLabelsFromEntries(entries: unknown): string[] {
  if (!Array.isArray(entries) || entries.length === 0) {
    return [];
  }

  const labels: string[] = [];

  for (const entry of entries) {
    if (typeof entry === 'string' && entry.trim() !== '') {
      labels.push(entry.trim());
      continue;
    }

    const objectLabel = labelFromEntry(entry);
    if (objectLabel) {
      labels.push(objectLabel);
    }
  }

  return labels;
}

export function resolveVerticalNames(payload: SchedulePayload): string[] {
  const fromNames = collectStringLabels(payload.verticalNames);
  if (fromNames.length > 0) {
    return fromNames;
  }

  if (typeof payload.vertical === 'string' && payload.vertical.trim() !== '') {
    return [payload.vertical.trim()];
  }

  return resolveLabelsFromEntries(payload.verticals);
}

export function resolveSystemNames(payload: SchedulePayload): string[] {
  const fromNames = collectStringLabels(payload.systemNames);
  if (fromNames.length > 0) {
    return fromNames;
  }

  if (typeof payload.system === 'string' && payload.system.trim() !== '') {
    return [payload.system.trim()];
  }

  return resolveLabelsFromEntries(payload.systems);
}

export function resolveSchedulePayload(payload: SchedulePayload): SchedulePayload {
  const flat = flattenSchedulePayload(payload);
  const verticalNames = resolveVerticalNames(flat);
  const systemNames = resolveSystemNames(flat);

  return {
    ...flat,
    ...(verticalNames.length > 0 ? { verticalNames } : {}),
    ...(systemNames.length > 0 ? { systemNames } : {}),
  };
}
